import { useEffect, useState } from 'react';
import { MessageSquare, Sparkles, Compass } from 'lucide-react';
import { ToolKey, TOOL_LIST, getTool } from '../data/tools';
import { useAuth } from '../contexts/AuthContext';

interface Props {
  value?: ToolKey;
  onChange?: (tool: ToolKey) => void;
  size?: 'sm' | 'md';
  persist?: boolean;
}

const ICONS: Record<ToolKey, typeof MessageSquare> = {
  chatgpt: MessageSquare,
  claude: Sparkles,
  gemini: Compass,
};

export default function ToolSwitcher({ value, onChange, size = 'sm', persist = true }: Props) {
  const { profile, updateProfile } = useAuth();
  const initial = value ?? getTool(profile?.preferred_tool).key;
  const [active, setActive] = useState<ToolKey>(initial);

  useEffect(() => {
    if (value) setActive(value);
  }, [value]);

  useEffect(() => {
    if (!value && profile?.preferred_tool) {
      setActive(getTool(profile.preferred_tool).key);
    }
  }, [profile?.preferred_tool, value]);

  const select = async (key: ToolKey) => {
    setActive(key);
    onChange?.(key);
    if (persist && profile && profile.preferred_tool !== key) {
      updateProfile({ preferred_tool: key });
    }
  };

  const padding = size === 'sm' ? 'px-2.5 py-1' : 'px-3 py-1.5';
  const text = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <div className="inline-flex items-center gap-1 p-1 rounded-full bg-warm-100 border border-ink-100">
      {TOOL_LIST.map((t) => {
        const Icon = ICONS[t.key];
        const isActive = active === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => select(t.key)}
            className={`inline-flex items-center gap-1.5 rounded-full font-medium transition ${padding} ${text} ${
              isActive ? 'bg-ink-900 text-warm-50 shadow-soft' : 'text-ink-600 hover:text-ink-900'
            }`}
            title={t.tagline}
          >
            <Icon className="w-3.5 h-3.5" /> {t.name}
          </button>
        );
      })}
    </div>
  );
}
